import { PickAnAttribute } from './PickAnAttribute.js';
import { KLogic } from './KLogic.js';

export let GeofireTools = {
  /**
   * Sets the geohash for a node, connection, or section
   * @param {string} type - the type of item to set the geohash for (one of: `nodes`, `connections`, or `sections`)
   * @param {Object} item - the item to set the geohash for
   * @param {string} itemId - the id of the "top-level" item to set the geohash for (either the node id or connection id)
   * @param {Object} jobStyles - the map style object for the job
   * @param {Object} update - the update object to write the geohash to
   * @param {Object} [options] - additional options for setting the geohash
   * @param {string} [options.geoPath] - the path to the geohash in the update object
   * @param {Object} [options.properties] - additional properties to set on the geohash object
   * @param {string} [options.sectionId] - the id of the section to set the geohash for (only used for sections)
   * @param {Object} [options.nodeConnections] - the connections for the node (only used for nodes)
   * @param {number[]} [options.location1] - the location of the first endpoint of the connection (only used for connections)
   * @param {number[]} [options.location2] - the location of the second endpoint of the connection (only used for connections)
   * @param {string} [options.nId1] - the id of the first endpoint node of the connection (only used for connections)
   * @param {string} [options.nId2] - the id of the second endpoint node of the connection (only used for connections)
   * @param {Object} [options.google] - the google object to use for distance calculations (only used for connections)
   * @example GeofireTools.setGeohash('nodes', node, new_node_id, this.jobStyles, update, { nodeConnections });
   * @example GeofireTools.setGeohash('connections', conn, connId, this.jobStyles, update, { location1: [latitude, longitude], location2: l2, nId1: conn.nId1, nId2: conn.nId2 })
   * @example GeofireTools.setGeohash('sections', section, connId, this.jobStyles, update, { sectionId })
   */
  setGeohash: function (type, item, itemId, jobStyles, update, options) {
    options = options || {};
    var location = options.location1 || [item.latitude, item.longitude];
    var attributes = type == 'sections' ? item.multi_attributes : item.attributes;
    this._setDefaultOptions(options);
    var types = { nodes: 'n', connections: 'c', sections: 's' };
    const itemKey = type == 'sections' ? options.sectionId : itemId;
    var itemGeo = this._getItemGeoStyle(type, item, jobStyles, itemKey);
    itemGeo.t = types[type];
    for (var key in options.properties) {
      itemGeo[key] = options.properties[key];
    }
    // Check if the type of item is a node
    if (type == 'nodes') {
      // Check if the options contains connections for the node, and add them to the object
      if (options.nodeConnections) {
        itemGeo.n = options.nodeConnections;
        // Also check to see if the node locations are already set in the update object
        // and if so, pull them into the itemGeo object instead
        for (let connId in options.nodeConnections) {
          let pathToTest = `${options.geoPath}${itemId}/n/${connId}`;
          if (update[pathToTest] != null) {
            itemGeo.n[connId] = update[pathToTest];
            // Remove from the main update
            delete update[pathToTest];
          }
        }
      }
    } else if (type == 'connections') {
      let goog = options.google || google;
      let distance = 0;
      if (item.breakpoints) {
        for (let i = 0; i < item.breakpoints.length; i++) {
          let breakpointLatLng = item.breakpoints[i];
          if (i == 0) {
            // if this is the first breakpoint then calc the distance from the beginning node
            distance +=
              goog.maps.geometry.spherical.computeDistanceBetween(
                new goog.maps.LatLng(location[0], location[1]),
                new goog.maps.LatLng(breakpointLatLng[0], breakpointLatLng[1])
              ) / 0.3048;
            // check if the beginning...is also the end *dun dun dun*
            if (i == item.breakpoints.length - 1) {
              // if this is the last breakpoint then calc the distance from the ending node
              distance +=
                goog.maps.geometry.spherical.computeDistanceBetween(
                  new goog.maps.LatLng(breakpointLatLng[0], breakpointLatLng[1]),
                  new goog.maps.LatLng(options.location2[0], options.location2[1])
                ) / 0.3048;
              // round the final value to one decimal place
              distance = this.round(distance, 1);
            } else {
              // if the beginning isn't also the end, then grab the next breakpoint and add that distance to the total
              let nextBreakpointLatLng = item.breakpoints[i + 1];
              distance +=
                goog.maps.geometry.spherical.computeDistanceBetween(
                  new goog.maps.LatLng(breakpointLatLng[0], breakpointLatLng[1]),
                  new goog.maps.LatLng(nextBreakpointLatLng[0], nextBreakpointLatLng[1])
                ) / 0.3048;
            }
          } else if (i == item.breakpoints.length - 1) {
            // if this is the last breakpoint then calc the distance from the ending node
            distance +=
              goog.maps.geometry.spherical.computeDistanceBetween(
                new goog.maps.LatLng(breakpointLatLng[0], breakpointLatLng[1]),
                new goog.maps.LatLng(options.location2[0], options.location2[1])
              ) / 0.3048;
            // round the final value to one decimal place
            distance = this.round(distance, 1);
          } else {
            let nextBreakpointLatLng = item.breakpoints[i + 1];
            distance +=
              goog.maps.geometry.spherical.computeDistanceBetween(
                new goog.maps.LatLng(breakpointLatLng[0], breakpointLatLng[1]),
                new goog.maps.LatLng(nextBreakpointLatLng[0], nextBreakpointLatLng[1])
              ) / 0.3048;
          }
        }
        itemGeo.b = item.breakpoints;
      } else {
        distance = this.round(
          goog.maps.geometry.spherical.computeDistanceBetween(
            new goog.maps.LatLng(location[0], location[1]),
            new goog.maps.LatLng(options.location2[0], options.location2[1])
          ) / 0.3048,
          1
        );
      }
      itemGeo.d = distance;
      itemGeo.l2 = options.location2;
      // Conn endpoint 2
      var item2Geo = this._getItemGeoStyle(type, item, jobStyles, itemKey);
      item2Geo.t = types[type];
      item2Geo.d = distance;
      item2Geo.l2 = location;
      if (item.breakpoints) item2Geo.b = item.breakpoints;
      this.update(options.geoPath + itemId + '~2', options.location2, item2Geo, 10, update);
      if (options.nId1) {
        this.updateNodeConnectionIndex(options.nId1, itemId, 1, update, options);
      }
      if (options.nId2) {
        this.updateNodeConnectionIndex(options.nId2, itemId, 2, update, options);
      }
    }
    this.update(
      options.geoPath + itemId + (options.sectionId ? ':' + options.sectionId : '') + (type == 'connections' ? '~1' : ''),
      location,
      itemGeo,
      10,
      update
    );
  },
  removeGeohash: function (type, item, itemId, update, options) {
    if (type == 'nodes') {
      // Remove the node's geohash.
      update[`${options.geoPath}${itemId}`] = null;
    } else if (type == 'connections') {
      // Remove the connection's geohash.
      update[`${options.geoPath}${itemId}~1`] = null;
      update[`${options.geoPath}${itemId}~2`] = null;
      // Loop sections and remove their geohash.
      Object.keys(item?.sections ?? {}).forEach((key) => {
        update[`${options.geoPath}${itemId}:${key}`] = null;
      });
      // Remove connection from node index lookup.
      this.updateNodeConnectionIndex(item?.node_id_1, itemId, null, update, options);
      this.updateNodeConnectionIndex(item?.node_id_1, itemId, null, update, options);
    }
  },
  updateNodeConnectionIndex: function (nodeId, connId, endpointIndex, update, options) {
    options = options || {};
    this._setDefaultOptions(options);
    let nodeUpdateObject = update[`${options.geoPath}${nodeId}`];
    // Check if the update object contains an object for the node.
    if (nodeUpdateObject) {
      if (!nodeUpdateObject.n) nodeUpdateObject.n = {};
      nodeUpdateObject.n[connId] = endpointIndex;
    } else if (nodeUpdateObject === undefined) {
      // If not, set the update as its own item.
      // We're checking explicity here that nodeUpdateObject is undefined, because, if it is null,that would
      // mean we are deleting the geohash for that node and thus should not try to set the endpoint index.
      update[`${options.geoPath}${nodeId}/n/${connId}`] = endpointIndex;
    }
  },
  _setDefaultOptions: function (options) {
    options = options || {};
    if (options.geoPath == null) {
      options.geoPath = 'geohash/';
    }
  },
  /**
   * Writes an update object for all the geohash styles for the given nodes and connections passed in
   * WARNING: if this function is used on a large amount of nodes and connections (like on all nodes and connection in a large job),
   * the update object will contain many paths to update because each style property is its own path. This can cause performance issues.
   * If you don't care about also updating the location (g) property of the geohash object, you should instead use getJobGeohash
   * and set the data at the geohash location
   * @param {String} jobId - the id of the job to update
   * @param {Object} jobStyles - the map style object for the job
   * @param {Object} nodes - an object of nodes to build style updates for
   * @param {Object} connections - an object of connections to build style updates for
   * @param {firebase=} database - an instance of firebase to use for the update. If none provided, FirebaseWorker will be used
   */
  updateStylesForItems: async function (jobId, jobStyles, nodes, connections, database) {
    if (jobId != null && jobId != '') {
      var update = {};
      for (let nodeId in nodes) {
        this.updateStyle('nodes', nodeId, nodes[nodeId], update, jobStyles);
      }
      for (let connId in connections) {
        this.updateStyle('connections', connId, connections[connId], update, jobStyles);
        for (var sectionId in connections[connId].sections) {
          this.updateStyle('sections', connId, connections[connId].sections[sectionId], update, jobStyles, sectionId);
        }
      }
      await (database || FirebaseWorker).ref(`photoheight/jobs/${jobId}`).update(update, (e) => {
        if (e) this.toast(e);
      });
    } else {
      console.warn('Update All Styles Failed, no jobId');
    }
  },
  updateStyle: function (type, itemId, item, update, jobStyles, sectionId, options) {
    options = options || {};
    this._setDefaultOptions(options);
    const itemKey = type == 'sections' ? sectionId : itemId;
    var geoStyle = this._getItemGeoStyle(type, item, jobStyles, itemKey);
    if (type == 'sections') {
      for (var property in geoStyle) {
        update[options.geoPath + itemId + ':' + sectionId + '/' + property] = geoStyle[property];
      }
    } else {
      for (var property in geoStyle) {
        if (type == 'connections') {
          update[options.geoPath + itemId + '~1/' + property] = geoStyle[property];
          update[options.geoPath + itemId + '~2/' + property] = geoStyle[property];
        } else {
          update[options.geoPath + itemId + '/' + property] = geoStyle[property];
        }
      }
    }
  },
  getJobGeohash: function (nodes, connections, jobStyles, options) {
    options = options || {};
    let geohash = {};
    let connLookup = {};
    for (var connId in connections) {
      if (nodes[connections[connId].node_id_1] && nodes[connections[connId].node_id_2]) {
        connLookup[connections[connId].node_id_1] = connLookup[connections[connId].node_id_1] || [];
        connLookup[connections[connId].node_id_1].push({ id: 1, connId });
        connLookup[connections[connId].node_id_2] = connLookup[connections[connId].node_id_2] || [];
        connLookup[connections[connId].node_id_2].push({ id: 2, connId });

        var l1 = [nodes[connections[connId].node_id_1].latitude, nodes[connections[connId].node_id_1].longitude];
        var l2 = [nodes[connections[connId].node_id_2].latitude, nodes[connections[connId].node_id_2].longitude];
        this.setGeohash(
          'connections',
          connections[connId],
          connId,
          jobStyles,
          geohash,
          Object.assign({}, options, { location1: l1, location2: l2, geoPath: '' })
        );
        for (var sectionId in connections[connId].sections) {
          var section = connections[connId].sections[sectionId];
          this.setGeohash('sections', section, connId, jobStyles, geohash, Object.assign({}, options, { sectionId, geoPath: '' }));
        }
      }
    }
    for (var nodeId in nodes) {
      var nodeConnections = {};
      if (connLookup[nodeId]) {
        for (var i = 0; i < connLookup[nodeId].length; i++) {
          nodeConnections[connLookup[nodeId][i].connId] = connLookup[nodeId][i].id;
        }
      }
      this.setGeohash('nodes', nodes[nodeId], nodeId, jobStyles, geohash, Object.assign({}, options, { geoPath: '', nodeConnections }));
    }
    return geohash;
  },
  updateLocation: function (key, location, precision, update) {
    var geohash = this._encodeGeohash(location, precision);
    update[key + '/.priority'] = geohash;
    update[key + '/g'] = geohash;
    update[key + '/l'] = location;
  },
  update: function (key, location, otherData, precision, update) {
    var geohash = this._encodeGeohash(location, precision);
    update[key] = otherData || {};
    update[key]['.priority'] = geohash;
    update[key]['g'] = geohash;
    update[key]['l'] = location;
  },
  _encodeGeohash: function (location, precision) {
    this._validateLocation(location);
    if (typeof precision !== 'undefined') {
      if (typeof precision !== 'number' || isNaN(precision)) {
        throw new Error('precision must be a number');
      } else if (precision <= 0) {
        throw new Error('precision must be greater than 0');
      } else if (precision > 22) {
        throw new Error('precision cannot be greater than 22');
      } else if (Math.round(precision) !== precision) {
        throw new Error('precision must be an integer');
      }
    }

    // Use the global precision default if no precision is specified
    precision = precision || 10;

    var g_BASE32 = '0123456789bcdefghjkmnpqrstuvwxyz';
    var latitudeRange = {
      min: -90,
      max: 90
    };
    var longitudeRange = {
      min: -180,
      max: 180
    };
    var hash = '';
    var hashVal = 0;
    var bits = 0;
    var even = 1;

    while (hash.length < precision) {
      var val = even ? location[1] : location[0];
      var range = even ? longitudeRange : latitudeRange;
      var mid = (range.min + range.max) / 2;

      /* jshint -W016 */
      if (val > mid) {
        hashVal = (hashVal << 1) + 1;
        range.min = mid;
      } else {
        hashVal = (hashVal << 1) + 0;
        range.max = mid;
      }
      /* jshint +W016 */

      even = !even;
      if (bits < 4) {
        bits++;
      } else {
        bits = 0;
        hash += g_BASE32[hashVal];
        hashVal = 0;
      }
    }

    return hash;
  },
  _validateLocation: function (location) {
    var error;

    if (!Array.isArray(location)) {
      error = 'location must be an array';
    } else if (location.length !== 2) {
      error = 'expected array of length 2, got length ' + location.length;
    } else {
      var latitude = location[0];
      var longitude = location[1];

      if (typeof latitude !== 'number' || isNaN(latitude)) {
        error = 'latitude must be a number';
      } else if (latitude < -90 || latitude > 90) {
        error = 'latitude must be within the range [-90, 90]';
      } else if (typeof longitude !== 'number' || isNaN(longitude)) {
        error = 'longitude must be a number';
      } else if (longitude < -180 || longitude > 180) {
        error = 'longitude must be within the range [-180, 180]';
      }
    }

    if (typeof error !== 'undefined') {
      throw new Error("Invalid GeoFire location '" + location + "': " + error);
    }
  },
  /*type = 'nodes' || 'connections' || 'sections' */
  _getItemGeoStyle: function (type, item, jobStyles, itemKey) {
    if (!item) return;

    // This is the return object which will be populated with the geo style
    let itemGeoStyle = {};
    let attributes = item.attributes || item.multi_attributes || {};

    // function will return upon finding first match, so put most pressing style first in array
    let model = jobStyles?.default?.[type] || [];
    let modelLength = model.length;
    let mainStyle = null;

    for (let i = 0; i < modelLength; i++) {
      if (this._doesStyleMatchVal(item, model[i], type, itemKey)) {
        // This is the base object for the style that contains properties
        // shared by all style types
        let itemStyle = {
          c: model[i].color,
          u: model[i].id,
          m: PickAnAttribute(attributes, 'field_completed') ? 1 : null,
          z: modelLength - i,
          // Icon stack
          is: []
        };
        if (item.feedback != null && item.feedback != '') {
          itemStyle.f = 1;
        }
        // If the type is a connection, then apply the connection-specific styles
        if (type == 'connections') {
          Object.assign(itemStyle, {
            p: model[i].pattern,
            w: model[i].width,
            o: 0.7,
            ci: model[i].connIcon ? model[i].connIcon : null,
            cic: model[i].connIconColor ? model[i].connIconColor : null,
            cis: model[i].connIconScale ? model[i].connIconScale : null
          });
        }
        // If the type is not a connection (node), then apply the node-specific styles
        else {
          Object.assign(itemStyle, {
            i: model[i].icon,
            s: model[i].size,
            xc: model[i].iconTextColor ? model[i].iconTextColor : null
          });
        }
        // If the main style has not been set yet, then set it
        if (!mainStyle) {
          mainStyle = itemStyle;
        }
        // Push a copy of the icon to the icon stack as well. The icon stack will
        // contain the main icon as well as any additional icons that should be
        // stacked. Eventually, we should remove the rest of the icon data from
        // the main level of the geohash item because it's redundant
        if (mainStyle.is) mainStyle.is.push(JSON.parse(JSON.stringify(itemStyle)));
        // If this style is not stackable, then we should break out of the loop
        if (!model[i].stackable) {
          break;
        }
      }
    }

    // Return the main style if one was found
    if (mainStyle) {
      // Sort icon stack by z property
      if (mainStyle.is) {
        mainStyle.is.sort((a, b) => (a.z || 0) - (b.z || 0));
      }
      itemGeoStyle = mainStyle;
    }
    // If no style was found, then try to apply the fallback style
    else {
      if (type == 'nodes') {
        let fallback = jobStyles?.default?.fallback?.nodes;
        if (fallback) itemGeoStyle = { c: fallback.color, i: fallback.icon, s: fallback.size, u: fallback.id };
        else
          itemGeoStyle = {
            c: '#f0f',
            i: 'katapult-map:circle',
            is: null,
            s: 9,
            u: 'mn',
            m: PickAnAttribute(attributes, 'field_completed') ? 1 : null,
            z: 1
          }; //miscnode
      } else if (type == 'connections') {
        let fallback = jobStyles?.default?.fallback?.connections;
        if (fallback) {
          let fallbackStyle = { c: fallback.color, p: fallback.pattern, w: fallback.width, o: 0.7, u: fallback.id };
          if (fallback.connIcon) {
            fallbackStyle.ci = fallback.connIcon;
            fallbackStyle.cis = fallback.connIconScale;
            fallbackStyle.cic = fallback.connIconColor;
          }
          itemGeoStyle = fallbackStyle;
        } else
          itemGeoStyle = {
            c: '#aa00ff',
            p: 'solid',
            is: null,
            w: 7,
            o: 0.7,
            u: 'mc',
            m: PickAnAttribute(attributes, 'field_completed') ? 1 : null,
            z: 0
          }; //miscconn
      } else if (type == 'sections') {
        if (itemKey == 'midpoint_section') {
          let fallback = jobStyles?.default?.fallback?.midpoint_sections || jobStyles?.default?.fallback?.sections;
          if (fallback) itemGeoStyle = { c: fallback.color, i: fallback.icon, s: fallback.size, u: fallback.id };
          else
            itemGeoStyle = {
              c: '#00f',
              i: 'katapult-map:triangle-up',
              is: null,
              s: 18,
              u: 'mms',
              m: PickAnAttribute(attributes, 'field_completed') ? 1 : null,
              z: 1
            }; //miscmidpointsection
        } else {
          let fallback = jobStyles?.default?.fallback?.sections;
          if (fallback) itemGeoStyle = { c: fallback.color, i: fallback.icon, s: fallback.size, u: fallback.id };
          else
            itemGeoStyle = {
              c: '#0ff',
              i: 'katapult-map:triangle-up',
              is: null,
              s: 18,
              u: 'ms',
              m: PickAnAttribute(attributes, 'field_completed') ? 1 : null,
              z: 1
            }; //miscsection
        }
      }
    }

    // Apply style adjustments
    if (type == 'nodes' && item.style_adjustments) {
      const adjustments = item.style_adjustments;
      if (adjustments.rotation) itemGeoStyle.r = adjustments.rotation;
    }

    return itemGeoStyle;
  },
  _doesStyleMatchVal: function (item, style, type, itemKey) {
    if (style.applies) return KLogic.compute(style.applies, { [type.slice(0, -1)]: item, itemKey });

    let attr = (item.attributes ?? item.multi_attributes)?.[style.attribute];
    let values = Object.values(attr || {});
    if (style.comparator === 'equals' || style.comparator == null)
      return values.some((val) => (val || '').toString() == (style.value || '').toString());
    else if (style.comparator === 'not equal' || style.comparator === 'does not equal')
      return values.some((val) => (val || '').toString() != '' && (val || '').toString() != (style.value || '').toString());
    else if (style.comparator === 'has') return values.length > 0;
    else if (style.comparator === 'does not have') return values.length <= 0;
  },
  round: function (value, exp) {
    if (typeof exp === 'undefined' || +exp === 0) return Math.round(value);

    value = +value;
    exp = +exp;

    if (isNaN(value) || !(typeof exp === 'number' && exp % 1 === 0)) return NaN;

    // Shift
    value = value.toString().split('e');
    value = Math.round(+(value[0] + 'e' + (value[1] ? +value[1] + exp : exp)));

    // Shift back
    value = value.toString().split('e');
    return +(value[0] + 'e' + (value[1] ? +value[1] - exp : -exp));
  },
  getNodeConnections: function (nodeId, connections) {
    let nodeConnections = {};
    for (let connId in connections) {
      if (nodeId == connections[connId].node_id_1) {
        nodeConnections[connId] = 1;
      }
      if (nodeId == connections[connId].node_id_2) {
        nodeConnections[connId] = 2;
      }
    }
    return nodeConnections;
  }
};
